if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      constructor() {
        super();
        this.input = this.querySelector('.quantity__input');
        this.currentVariant = this.querySelector('.product-variant-id');
        this.submitButton = this.querySelector('[type="submit"]');

        this.onQuantityInput = this.onQuantityInput.bind(this);
        this.onQuantityChange = this.onQuantityChange.bind(this);
      }

      cartUpdateUnsubscriber = undefined;
      variantChangeUnsubscriber = undefined;

      connectedCallback() {
        if (!this.input) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        // quantity listeners
        this.input.addEventListener('input', this.onQuantityInput);
        this.input.addEventListener('change', this.onQuantityChange);

        this.setQuantityBoundries();

        // sync image on load
        this.updateImageByQuantity();

        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const sectionId = this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section;
          if (event.data.sectionId !== sectionId) return;

          this.updateQuantityRules(event.data.sectionId, event.data.html);
          this.setQuantityBoundries();

          // variant can change media set
          this.updateImageByQuantity();
        });
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();

        if (this.input) {
          this.input.removeEventListener('input', this.onQuantityInput);
          this.input.removeEventListener('change', this.onQuantityChange);
        }
      }

      onQuantityInput() {
        this.updateImageByQuantity();
      }

      onQuantityChange() {
        this.updateImageByQuantity();
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.input.dataset.cartQuantity ? parseInt(this.input.dataset.cartQuantity) : 0,
          min: this.input.dataset.min ? parseInt(this.input.dataset.min) : 1,
          max: this.input.dataset.max ? parseInt(this.input.dataset.max) : null,
          step: this.input.step ? parseInt(this.input.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.input.min = min;
        if (max) this.input.max = max;
        else this.input.removeAttribute('max');

        this.input.value = min;
        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);

        this.updateImageByQuantity();
      }

      fetchQuantityRules() {
        if (!this.currentVariant || !this.currentVariant.value) return;

        const spinner = this.querySelector('.quantity__rules-cart .loading__spinner');
        if (spinner) spinner.classList.remove('hidden');

        fetch(`${this.dataset.url}?variant=${this.currentVariant.value}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
            this.setQuantityBoundries();
            this.updateImageByQuantity();
          })
          .catch((e) => console.error(e))
          .finally(() => {
            if (spinner) spinner.classList.add('hidden');
          });
      }

      updateQuantityRules(sectionId, html) {
        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        if (!quantityFormUpdated) return;

        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;

          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) current.setAttribute(attribute, valueUpdated);
              else current.removeAttribute(attribute);
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      /**
       * Qty -> image mapping by ALT prefix number (1..24):
       * ALT examples:
       *   "1 Stück PRODUCT NAME"
       *   "2 Stück PRODUCT NAME"
       *   "24 Stück PRODUCT NAME"
       *
       * Behavior:
       * - If exact qty match exists in ALT => switch to that media
       * - If no match => keep current image (no change)
       * - Caps qty > 24 to 24
       *
       * IMPORTANT: we ONLY activate media via media-gallery API or by clicking LEFT thumbnails,
       * never clicking the main image (to avoid zoom).
       */
      updateImageByQuantity() {
        if (!this.input) return;

        const qtyRaw = parseInt(this.input.value || '1', 10);
        let qty = Number.isFinite(qtyRaw) && qtyRaw > 0 ? qtyRaw : 1;

        // ✅ cap to 24 as requested
        if (qty > 24) qty = 24;

        // limit scope to this product section
        const scope =
          this.closest('[data-section-id]') ||
          this.closest('.product') ||
          this.closest('.product-section') ||
          document;

        const mediaGallery = scope.querySelector('media-gallery') || document.querySelector('media-gallery');

        // ✅ More robust thumbnails root (inside media-gallery if possible)
        const thumbnailsRoot =
          (mediaGallery &&
            (mediaGallery.querySelector('[id^="GalleryThumbnails"]') ||
              mediaGallery.querySelector('[id^="Slider-Thumbnails-"]'))) ||
          scope.querySelector('.thumbnail-list') ||
          scope.querySelector('[id^="GalleryThumbnails"]') ||
          scope.querySelector('[id^="Slider-Thumbnails-"]') ||
          scope.querySelector('[data-thumbnails]') ||
          mediaGallery ||
          scope;

        const thumbButtons = this.getThumbnailButtons(thumbnailsRoot);
        if (!thumbButtons.length) return;

        // Helpers
        const parseLeadingQty = (altText) => {
          if (!altText) return null;
          const m = String(altText).trim().match(/^(\d+)\b/);
          if (!m) return null;
          const n = parseInt(m[1], 10);
          if (!Number.isFinite(n)) return null;
          if (n < 1 || n > 24) return null;
          return n;
        };

        // Normalize media id for Dawn API:
        // - Sometimes data-target is like "SECTIONID-1234567890"
        // - setActiveMedia often expects just "1234567890"
        const normalizeMediaId = (raw) => {
          if (!raw) return null;
          const s = String(raw);
          if (/^\d+$/.test(s)) return s;

          const m = s.match(/(\d+)(?!.*\d)/);
          return m ? m[1] : null;
        };

        // Build qty -> { button, mediaIdNormalized, mediaIdRaw }
        const qtyMap = new Map();

        for (const btn of thumbButtons) {
          const img = btn.querySelector('img');

          const alt =
            img?.getAttribute('alt') ||
            img?.getAttribute('data-alt') ||
            img?.dataset?.alt ||
            '';

          const n = parseLeadingQty(alt);
          if (!n) continue;

          const rawId =
            btn?.dataset?.mediaId ||
            btn?.getAttribute('data-media-id') ||
            btn?.dataset?.target ||
            btn?.getAttribute('data-target') ||
            btn?.getAttribute('aria-controls') ||
            null;

          const normalized = normalizeMediaId(rawId);

          if (!qtyMap.has(n)) {
            qtyMap.set(n, { button: btn, mediaIdRaw: rawId, mediaIdNormalized: normalized });
          }
        }

        const target = qtyMap.get(qty);
        if (!target) return;

        // 1) Prefer media-gallery API
        if (mediaGallery && typeof mediaGallery.setActiveMedia === 'function') {
          const idForApi = target.mediaIdNormalized || target.mediaIdRaw;
          if (idForApi) {
            try {
              mediaGallery.setActiveMedia(idForApi, false);
              return;
            } catch (e) {
              // fallback below
            }
          }
        }

        // 2) Fallback: click thumbnail button
        if (target.button && typeof target.button.click === 'function') {
          target.button.click();
        }
      }

      /**
       * Returns an ordered list of thumbnail buttons from the left column.
       * We intentionally avoid any selectors that could hit the main media area.
       */
      getThumbnailButtons(thumbnailsRoot) {
        if (!thumbnailsRoot) return [];

        let btns = Array.from(
          thumbnailsRoot.querySelectorAll(
            [
              '.thumbnail-list__item button',
              '[id^="GalleryThumbnails"] button',
              '[id^="Slider-Thumbnails-"] button',
              'button[data-media-id]',
              'button[data-target]',
              'button[aria-controls]',
            ].join(',')
          )
        ).filter((b) => b && !b.disabled);

        if (!btns.length) {
          btns = Array.from(
            thumbnailsRoot.querySelectorAll(
              [
                '.thumbnail-list__item a',
                '[id^="GalleryThumbnails"] a',
                '[id^="Slider-Thumbnails-"] a',
                'a[data-media-id]',
                'a[data-target]',
                'a[aria-controls]',
              ].join(',')
            )
          );
        }

        return btns;
      }
    }
  );
}


